import { List, Record } from 'immutable';
import type { Reducer } from 'redux';
import { handleActions } from 'redux-actions';

import { DEFAULT_CITY_MODE } from '@/constants';
import * as userActions from './user-actions';
import { LOCALSTORAGE_MAP_INFOBOX_EXPAND } from './user-constants';

export class InitState extends Record({
  isPreAuthenticationPhase: true,
  isAuthenticated: false,
  username: null,
  userFirstName: null,
  userLastName: null,
  currentCity: null,
  cityMode: DEFAULT_CITY_MODE,
  contactEmailInFlight: false,
  helpCenterEmailInFlight: false,
  helpCenterEmailSuccessful: false,
  defaultCity: null,
  authorizedCities: {},
  attributes: {},
  showAll: null, // allows admin user to choose to see customer mode in trial cities
  userRights: {},
}) {
  /* required during city switch. Everything about the user should stay the same
     except for current City which we reset back to default value on login */
  purgeState() {
    localStorage.removeItem(LOCALSTORAGE_MAP_INFOBOX_EXPAND);
    return this.remove('currentCity').remove('cityMode');
  }
}

const initState = new InitState();

export const userReducer: Reducer<InitState> = handleActions(
  {
    [userActions.SET_IS_PRE_AUTHENTICATION_PHASE]: (state, action) => {
      const { value } = action.payload;

      return state.set('isPreAuthenticationPhase', value);
    },

    [userActions.SET_IS_AUTHENTICATED]: (state, action) => {
      const { cognitoUser } = action.payload;
      const isAuthenticated = cognitoUser?.signInUserSession.isValid();

      if (isAuthenticated) {
        return state
          .set('isAuthenticated', isAuthenticated)
          .set('username', cognitoUser.username);
      }
      return state.set('isAuthenticated', false).set('username', null);
    },

    [userActions.SET_CONTACT_EMAIL_INFLIGHT]: (state, action) => {
      const { status } = action.payload;
      return state.set('contactEmailInFlight', status);
    },

    [userActions.SET_HELP_CENTER_EMAIL_INFLIGHT]: (state, action) => {
      const { status } = action.payload;
      return state.set('helpCenterEmailInFlight', status);
    },

    [userActions.SET_HELP_CENTER_EMAIL_SUCCESSFUL]: (state, action) => {
      const { status } = action.payload;
      return state.set('helpCenterEmailSuccessful', status);
    },

    [userActions.SET_CURRENT_CITY]: (state, action) => {
      const { city } = action.payload;

      return state.set('currentCity', city);
    },

    [userActions.STORE_USER_ATTRIBUTES]: (state, action) => {
      const { attributes } = action.payload;
      return state.set('attributes', attributes);
    },

    [userActions.SET_AUTHORIZED_CITIES]: (state, action) => {
      const { authorizedCities } = action.payload;
      return state.set('authorizedCities', authorizedCities);
    },

    [userActions.SET_USER_FIRST_NAME]: (state, action) => {
      const { firstName } = action.payload;
      return state.set('userFirstName', firstName);
    },

    [userActions.SET_USER_LAST_NAME]: (state, action) => {
      const { lastName } = action.payload;
      return state.set('userLastName', lastName);
    },

    [userActions.SERVER_RETURNED_USER_RIGHTS]: (state, action) => {
      const { userRights } = action.payload;
      return state.set('userRights', userRights);
    },
  },
  initState
);
